import { supabase } from './supabaseClient';
import { SubscriptionService } from './subscriptionService';
import { USAGE_METRICS } from '../types/subscription';
import { EmailService } from './sendEmailNotification';

interface CreateReviewRequestParams {
  userId: string;
  type: 'document' | 'website';
  fileUrl?: string;
  websiteUrl?: string;
  reviewLink: string;
  fileType?: string;
  deadline?: string | null;
  timeZone: string;
  reviewerFirstName: string;
  reviewerLastName: string;
  reviewerEmail: string;
  title: string;
  userEmail?: string; // Add user email for notifications
}

export const createReviewRequest = async (params: CreateReviewRequestParams) => {
  const {
    userId,
    type,
    fileUrl,
    websiteUrl,
    reviewLink,
    fileType,
    deadline,
    timeZone,
    reviewerFirstName,
    reviewerLastName,
    reviewerEmail,
    title,
    userEmail
  } = params;

  try {
    // Check if user can create more reviews
    let userProfile = await SubscriptionService.getUserProfile(userId);
    
    // If user profile doesn't exist, create one with default free plan
    if (!userProfile) {
      console.log('User profile not found, creating new profile with free plan');
      try {
        userProfile = await SubscriptionService.ensureUserProfile(userId, userEmail || 'user@example.com');
      } catch (profileError) {
        console.error('Error creating user profile:', profileError);
        throw new Error('Failed to initialize user account. Please try again.');
      }
    }

    if (!userProfile) {
      throw new Error('User profile not found');
    }

    // TEMPORARILY DISABLED: Subscription limits are disabled
    // const limitCheck = await SubscriptionService.checkFeatureLimit(
    //   userProfile.id,
    //   'reviews',
    //   USAGE_METRICS.REVIEWS_CREATED
    // );

    // if (!limitCheck.allowed) {
    //   throw new Error(`You've reached your limit of ${limitCheck.limit} reviews per month. Please upgrade your plan to create more reviews.`);
    // }

    // Create the review request using clerk_user_id
    const { data: reviewRequest, error: reviewError } = await supabase
      .from('review_requests')
      .insert({
        clerk_user_id: userId,
        type,
        file_url: fileUrl,
        website_url: websiteUrl,
        review_link: reviewLink,
        file_type: fileType,
        deadline,
        time_zone: timeZone,
        title,
        status: 'open',
        user_email: userEmail
      })
      .select()
      .single();

    if (reviewError) {
      console.error('Error creating review request:', reviewError);
      throw new Error('Failed to create review request');
    }

    // NOTE: Reviewer creation is now handled in App.tsx with more complete information
    // including personalized links and email notifications

    // Track usage
    try {
      await SubscriptionService.incrementUsage(userProfile.id, USAGE_METRICS.REVIEWS_CREATED);
    } catch (usageError) {
      console.error('Error tracking usage:', usageError);
      // Don't fail the review creation if usage tracking fails
    }

    return reviewRequest;
  } catch (error) {
    console.error('Error in createReviewRequest:', error);
    
    // Provide user-friendly error messages
    if (error instanceof Error) {
      if (error.message.includes('JWT') || error.message.includes('Authentication expired')) {
        throw new Error('Your session has expired. Please refresh the page and try again.');
      }
      throw error;
    }
    
    throw new Error('An unexpected error occurred. Please try again.');
  }
}; 